import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getOnboardingLeads,
  updateOnboardingLead,
  convertOnboardingLead,
  getWebsiteLeads,
  updateWebsiteLead,
  type OnboardingLead,
  type LeadWorkedStatus,
  type WebsiteLead,
  type CommercialStatus,
} from '@/services'
import { AppButton, EmptyState, FilterChip, StatusBadge, PlanBadge, TableSkeleton, Tabs, Drawer, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { relTime } from '@/lib/utils'

export function LeadsPage() {
  const [tab, setTab] = useState('onboarding')
  const { data: onbResult } = useQuery({ queryKey: ['onboarding-leads', 'count'], queryFn: () => getOnboardingLeads({ pageSize: 200 }) })
  const { data: webResult } = useQuery({ queryKey: ['website-leads', 'count'], queryFn: () => getWebsiteLeads({ pageSize: 200 }) })
  const onbTotal = onbResult?.count ?? onbResult?.data.length ?? 0
  const webTotal = webResult?.count ?? webResult?.data.length ?? 0

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Clientes</div>
          <h1>Leads</h1>
          <div className="subtitle">Onboarding del signup web + contactos comerciales.</div>
        </div>
        <div className="page-actions">
          <AppButton variant="secondary" icon="download">Exportar</AppButton>
        </div>
      </div>

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'onboarding', label: 'Onboarding (signup web)', icon: 'inbox', count: onbTotal },
          { id: 'contact', label: 'Contacto comercial', icon: 'send', count: webTotal },
        ]}
      />

      {tab === 'onboarding' ? <OnboardingLeadsTable /> : <ContactLeadsTable />}
    </>
  )
}

const ONB_FILTERS: Array<{ id: string; label: string }> = [
  { id: 'pending', label: 'Sin trabajar' },
  { id: 'contacted', label: 'Contactados' },
  { id: 'converted', label: 'Convertidos' },
  { id: 'lost', label: 'Perdidos' },
  { id: 'all', label: 'Todos' },
]

function OnboardingLeadsTable() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState('pending')
  const [open, setOpen] = useState<OnboardingLead | null>(null)

  const { data: result, isLoading } = useQuery({
    queryKey: ['onboarding-leads', filter],
    queryFn: () => getOnboardingLeads({ workedStatus: filter === 'all' ? undefined : (filter as LeadWorkedStatus), pageSize: 100 }),
  })
  const rows = result?.data || []

  const invalidate = () => qc.invalidateQueries({ queryKey: ['onboarding-leads'] })

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: LeadWorkedStatus }) => updateOnboardingLead(id, { worked_status: status }),
    onSuccess: () => { toast.success('Lead actualizado'); invalidate(); setOpen(null) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo actualizar'),
  })
  const convertMutation = useMutation({
    mutationFn: (id: string) => convertOnboardingLead(id),
    onSuccess: () => { toast.success('Lead convertido: account + tenant'); invalidate(); setOpen(null) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo convertir'),
  })

  return (
    <div className="table-wrap">
      <div className="table-toolbar">
        {ONB_FILTERS.map((f) => (
          <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} label={f.label} />
        ))}
      </div>
      {isLoading ? (
        <TableSkeleton columns={5} rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState icon="inbox" title="No hay leads en esta vista" desc="¡Buen trabajo! No quedan leads sin trabajar." />
      ) : (
        <table className="table">
          <thead>
            <tr><th>Email · negocio</th><th>Estado</th><th>Plan elegido</th><th>Cuándo</th><th /></tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} onClick={() => setOpen(l)} {...rowActivation(() => setOpen(l))}>
                <td>
                  <div className="cell-primary">{l.email || '—'}</div>
                  <div className="cell-secondary">{l.business_name || '—'}</div>
                </td>
                <td><StatusBadge status={l.worked_status} size="sm" /></td>
                <td><PlanBadge plan={l.plan_slug || (l.plan_chosen as string | undefined)} /></td>
                <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(l.created_at)}</span></td>
                <td style={{ textAlign: 'right' }}><Icon name="chevronRight" size={14} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? `Lead — ${open.business_name || open.email}` : ''}>
        {open ? (
          <div className="col gap-4">
            <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
              <StatusBadge status={open.worked_status} size="sm" />
              <PlanBadge plan={open.plan_slug || (open.plan_chosen as string | undefined)} />
              <span className="muted" style={{ fontSize: 12.5, marginLeft: 'auto' }}>Creado {relTime(open.created_at)}</span>
            </div>
            <div className="card">
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '110px 1fr' }}>
                  <dt>Email</dt><dd>{open.email || '—'}</dd>
                  <dt>Negocio</dt><dd>{open.business_name || '—'}</dd>
                  <dt>Contacto</dt><dd>{open.contact_name || '—'}</dd>
                  <dt>Teléfono</dt><dd>{open.phone || '—'}</dd>
                  <dt>Plan</dt><dd><PlanBadge plan={open.plan_slug || (open.plan_chosen as string | undefined)} /></dd>
                </dl>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="t-eyebrow" style={{ marginBottom: 10 }}>Acciones</div>
                <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                  <AppButton variant="secondary" size="sm" icon="send" onClick={() => statusMutation.mutate({ id: open.id, status: 'contacted' })}>Marcar contactado</AppButton>
                  <AppButton variant="secondary" size="sm" icon="x" onClick={() => statusMutation.mutate({ id: open.id, status: 'lost' })}>Marcar perdido</AppButton>
                  <AppButton variant="success" size="sm" icon="arrowRight" onClick={() => convertMutation.mutate(open.id)}>Convertir</AppButton>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}

const CONTACT_FILTERS: Array<{ id: string; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'new', label: 'Nuevos' },
  { id: 'contacted', label: 'Contactados' },
  { id: 'qualified', label: 'Calificados' },
  { id: 'discarded', label: 'Descartados' },
]

const COMMERCIAL_TONE: Record<CommercialStatus, string> = {
  new: 'warning', contacted: 'info', qualified: 'success', discarded: 'neutral',
}

function ContactLeadsTable() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState('all')
  const [open, setOpen] = useState<WebsiteLead | null>(null)

  const { data: result, isLoading } = useQuery({
    queryKey: ['website-leads', filter],
    queryFn: () => getWebsiteLeads({ commercialStatus: filter === 'all' ? undefined : (filter as CommercialStatus), pageSize: 100 }),
  })
  const rows = result?.data || []

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: CommercialStatus }) => updateWebsiteLead(id, status),
    onSuccess: () => { toast.success('Lead actualizado'); qc.invalidateQueries({ queryKey: ['website-leads'] }); setOpen(null) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo actualizar'),
  })

  return (
    <div className="table-wrap">
      <div className="table-toolbar">
        {CONTACT_FILTERS.map((f) => (
          <FilterChip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} label={f.label} />
        ))}
      </div>
      {isLoading ? (
        <TableSkeleton columns={5} rows={8} />
      ) : rows.length === 0 ? (
        <EmptyState icon="send" title="Sin contactos comerciales" desc="Los formularios de contacto de bisne.app caen acá." />
      ) : (
        <table className="table">
          <thead><tr><th>Nombre · negocio</th><th>Contacto</th><th>Mensaje</th><th>Estado</th><th>Cuándo</th></tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} onClick={() => setOpen(l)} {...rowActivation(() => setOpen(l))}>
                <td>
                  <div className="cell-primary">{l.name || '—'}</div>
                  <div className="cell-secondary">{l.business || '—'}</div>
                </td>
                <td><span style={{ fontSize: 12.5 }}>{l.email}<br />{l.phone}</span></td>
                <td style={{ maxWidth: 280 }}><span style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{(l.message || '').slice(0, 80)}{(l.message || '').length > 80 ? '…' : ''}</span></td>
                <td><span className={`badge badge-${COMMERCIAL_TONE[l.commercial_status]} badge-sm`}>{l.commercial_status}</span></td>
                <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(l.created_at)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Drawer open={!!open} onClose={() => setOpen(null)} title={open ? `Lead — ${open.name || open.business}` : ''}>
        {open ? (
          <div className="col gap-4">
            <div className="card">
              <div className="card-body">
                <dl className="dl" style={{ gridTemplateColumns: '90px 1fr' }}>
                  <dt>Nombre</dt><dd>{open.name || '—'}</dd>
                  <dt>Negocio</dt><dd>{open.business || '—'}</dd>
                  <dt>Email</dt><dd>{open.email || '—'}</dd>
                  <dt>Teléfono</dt><dd>{open.phone || '—'}</dd>
                  <dt>Origen</dt><dd>{open.source || '—'}</dd>
                </dl>
              </div>
            </div>
            {open.message ? (
              <div className="card">
                <div className="card-body">
                  <div className="t-eyebrow" style={{ marginBottom: 8 }}>Mensaje</div>
                  <p style={{ fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{open.message}</p>
                </div>
              </div>
            ) : null}
            <div className="card">
              <div className="card-body">
                <div className="t-eyebrow" style={{ marginBottom: 10 }}>Estado comercial</div>
                <div className="row gap-2" style={{ flexWrap: 'wrap' }}>
                  <AppButton variant="secondary" size="sm" onClick={() => statusMutation.mutate({ id: open.id, status: 'contacted' })}>Contactado</AppButton>
                  <AppButton variant="success" size="sm" icon="check" onClick={() => statusMutation.mutate({ id: open.id, status: 'qualified' })}>Calificar</AppButton>
                  <AppButton variant="ghost" size="sm" onClick={() => statusMutation.mutate({ id: open.id, status: 'discarded' })}>Descartar</AppButton>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  )
}
